/**
 * Review — the two small decisions behind Then vs Now, kept pure so they can
 * be tested without a database:
 *
 *  1. Which cycles to offer. A cycle report belongs to a prioritization cycle
 *     (`cycle_reports.cycle_id` references `degap_cycles`), so the picker lists
 *     those cycles, and only the ones that actually have a report.
 *  2. What to tell the reader when there are no changes to list, without
 *     claiming "nothing changed" when we simply have nothing to compare.
 */

import type { AmendmentLogEntry } from '@/stores/prioritization/cycleReportStore';

export interface ReviewCycle {
  id: string;
  name: string;
}

interface CycleLike {
  id: string;
  name?: string | null;
  updatedAt?: Date | string | null;
  createdAt?: Date | string | null;
}

function stamp(value: Date | string | null | undefined): number {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Cycles that have a current report, most recently touched first. */
export function reviewCycles(
  cycles: CycleLike[],
  cycleIdsWithReport: ReadonlySet<string>,
): ReviewCycle[] {
  return cycles
    .filter((c) => cycleIdsWithReport.has(c.id))
    .map((c, index) => ({ c, index }))
    .sort((a, b) => {
      const diff = stamp(b.c.updatedAt ?? b.c.createdAt) - stamp(a.c.updatedAt ?? a.c.createdAt);
      return diff !== 0 ? diff : a.index - b.index;
    })
    .map(({ c }) => ({ id: c.id, name: c.name?.trim() || 'Untitled cycle' }));
}

/** Why there is nothing to list under "What changed". */
export type NoChangeReason =
  /** This is the first published version, so there is no "then". */
  | 'first_version'
  /** An earlier version exists and the recorded comparison found no difference. */
  | 'unchanged'
  /** An earlier version exists but no comparison was recorded for this one. */
  | 'not_recorded';

export const NO_CHANGE_MESSAGE: Record<NoChangeReason, string> = {
  first_version: 'There is no earlier version to compare with yet.',
  unchanged: 'Nothing has changed since this was published.',
  not_recorded:
    "This version did not record what changed, so it can't be compared with the one before it.",
};

/**
 * The changes to show, or the reason there are none.
 *
 * The comparison is the amendment log the server wrote when this version was
 * ratified. An empty log written against an earlier version is a real answer
 * ("no difference"); a missing log is not, and is never reported as one.
 */
export function describeChanges(input: {
  version: number | undefined;
  amendmentLog: AmendmentLogEntry[] | undefined;
  history: Array<{ version: number }>;
}): { changes: AmendmentLogEntry[]; noChange: NoChangeReason | null } {
  const changes = input.amendmentLog ?? [];
  if (changes.length > 0) return { changes, noChange: null };

  const current = input.version ?? Number.MAX_SAFE_INTEGER;
  const hasEarlierVersion = input.history.some((h) => h.version < current);
  if (!hasEarlierVersion) return { changes, noChange: 'first_version' };
  if (input.amendmentLog === undefined) return { changes, noChange: 'not_recorded' };
  return { changes, noChange: 'unchanged' };
}
